/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message, DatePicker, TimePicker, Select } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Calendar, momentLocalizer } from "react-big-calendar";
import CustomBreadcrumb from "../../common/CustomBreadcrumb";
import { BreadCrum } from "../../common";
import Loader from "../Loder/loader";
import {
    CalanderIcon,
    ChevronIcon,
    TimeIcon,
    TimezoneSelectedIcon,
    InfoIcon,
    RestartIcon,
    Trash,
} from "../../assets/icon";
import CsvUploadDragger from "../Dragger/CsvUploadDragger";
import CampaignModal from "../../common/CampaignModal";
import {
    getAiCampaignDetailsHandler,
} from "../../redux/action/getAiCampaignDetailsSlice";
import { API_URL } from "../../services/http";
import {
    MODULE_ACCESS_DENIED_MESSAGE,
    getUploadIssueErrorFileUrl,
    getUploadIssueMessage,
    isUploadIssueResponse,
} from "../../common/apiResponse";
import { getItem } from "../../common/localStorage";
import {
  FILE_UPLOAD_MODULES,
  appendFileUploadModuleContext,
} from "../../common/productFileUpload";
import {
    buildInitialRestartForm,
    buildPreferredOutreachFromApi,
    buildRestartCampaignUpdatePayload,
    PREFERRED_OUTREACH_OPTIONS,
    TIMEZONE_OPTIONS,
} from "./manageCampaignRestartHelpers";
import { updateAiCampaignHandler, updateAiCampaignAction } from "../../redux/action/updateAiCampaignSlice";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const localizer = momentLocalizer(moment);
const RESTART_REQUIRED_FIELD_ORDER = [
    "coverageStartDate",
    "campaignStartDate",
    "campaignStartTime",
    "campaignEndDate",
    "campaignEndTime",
];

const TIMEZONE_TO_IANA = {
    CST: "America/Chicago",
    PST: "America/Los_Angeles",
    EST: "America/New_York",
    MST: "America/Denver",
};

/** Combine campaign date + TimePicker value into one dayjs (time-of-day from `timeVal`). */
const combineCampaignDateTime = (dateVal, timeVal, timezoneCode = "CST") => {
    if (!dateVal || !timeVal) return null;
    const datePart = dayjs(dateVal).format("YYYY-MM-DD");
    const timePart = dayjs(timeVal).format("HH:mm:ss");
    const timezoneName = TIMEZONE_TO_IANA[timezoneCode] || TIMEZONE_TO_IANA.CST;
    const combined = dayjs.tz(
        `${datePart} ${timePart}`,
        "YYYY-MM-DD HH:mm:ss",
        timezoneName,
    );
    return combined.isValid() ? combined : null;
};

const isSunday = (dateValue) => dayjs(dateValue).day() === 0;

const getSundayDateStringsInRange = (rangeStart, rangeEnd) => {
    if (!rangeStart || !rangeEnd || rangeStart.isAfter(rangeEnd)) return [];
    const sundayDates = [];
    let cursor = rangeStart.startOf("day");
    while (cursor.isBefore(rangeEnd) || cursor.isSame(rangeEnd, "day")) {
        if (isSunday(cursor)) {
            sundayDates.push(cursor.format("YYYY-MM-DD"));
        }
        cursor = cursor.add(1, "day");
    }
    return sundayDates;
};

const getTimezoneNow = (timezoneCode = "CST") => {
    const timezoneName = TIMEZONE_TO_IANA[timezoneCode] || TIMEZONE_TO_IANA.CST;
    return dayjs().tz(timezoneName);
};

const toTimezoneDayStart = (value, timezoneCode = "CST") => {
    if (!value) return null;
    const datePart = dayjs(value).format("YYYY-MM-DD");
    const timezoneName = TIMEZONE_TO_IANA[timezoneCode] || TIMEZONE_TO_IANA.CST;
    return dayjs.tz(datePart, "YYYY-MM-DD", timezoneName).startOf("day");
};

const validateAtLeastOneNonBlackoutDay = (campaignStartDate, campaignEndDate, blackoutDates, timezoneCode = "CST") => {
    if (!campaignStartDate || !campaignEndDate) return true;
    const start = toTimezoneDayStart(campaignStartDate, timezoneCode);
    const end = toTimezoneDayStart(campaignEndDate, timezoneCode);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return true;

    const totalDays = end.diff(start, "day") + 1;
    const selectedDays = Array.isArray(blackoutDates) ? blackoutDates.length : 0;
    return selectedDays < totalDays;
};

const buildDisabledPastTime = (selectedDate, timezoneCode = "CST") => {
    if (!selectedDate) return {};
    const selectedDay = toTimezoneDayStart(selectedDate, timezoneCode);
    const now = getTimezoneNow(timezoneCode);
    if (!selectedDay || !selectedDay.isSame(now.startOf("day"), "day")) return {};
    const currentHour = now.hour();
    const currentMinute = now.minute();

    return {
        disabledHours: () => Array.from({ length: 24 }, (_, h) => h).filter((h) => h < currentHour),
        disabledMinutes: (selectedHour) =>
            selectedHour === currentHour
                ? Array.from({ length: 60 }, (_, m) => m).filter((m) => m < currentMinute)
                : [],
    };
};

const toSlotMinutes = (slot) => {
    const start = dayjs(`1970-01-01 ${slot?.startTime}`);
    const end = dayjs(`1970-01-01 ${slot?.endTime}`);
    if (!start.isValid() || !end.isValid()) return null;
    return {
        startMinutes: start.hour() * 60 + start.minute(),
        endMinutes: end.hour() * 60 + end.minute(),
    };
};

const isSameSlot = (firstSlot, secondSlot) =>
    firstSlot?.timezone === secondSlot?.timezone &&
    firstSlot?.startTime === secondSlot?.startTime &&
    firstSlot?.endTime === secondSlot?.endTime;

const slotsOverlap = (firstSlot, secondSlot) => {
    if (firstSlot?.timezone !== secondSlot?.timezone) return false;
    const firstMinutes = toSlotMinutes(firstSlot);
    const secondMinutes = toSlotMinutes(secondSlot);
    if (!firstMinutes || !secondMinutes) return false;
    return firstMinutes.startMinutes < secondMinutes.endMinutes && firstMinutes.endMinutes > secondMinutes.startMinutes;
};

const formatOutreachSlotLabel = (slot) => {
    if (!slot?.startTime || !slot?.endTime || !slot?.timezone) return "";
    const start = dayjs(`1970-01-01 ${slot.startTime}`);
    const end = dayjs(`1970-01-01 ${slot.endTime}`);
    if (!start.isValid() || !end.isValid()) return "";
    return `${start.format("h:mm A")} - ${end.format("h:mm A")} ${slot.timezone}`;
};

const RestartCampaignPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { campaignId, refBrandId } = location.state || {};

    const updateAiCampaignState = useSelector((state) => state?.updateAiCampaign);
    const campaignDetailsState = useSelector((state) => state?.getAiCampaignDetails);

    const [restartForm, setRestartForm] = useState(null);
    const [restartFormKey, setRestartFormKey] = useState(0);
    const [restartStartTzOpen, setRestartStartTzOpen] = useState(false);
    const [restartEndTzOpen, setRestartEndTzOpen] = useState(false);
    const [restartValidation, setRestartValidation] = useState({});
    /** Mirrors Formik submitCount — preferred outreach error only after Restart is attempted. */
    const [restartSubmitCount, setRestartSubmitCount] = useState(0);
    const [preferredOutreachOptions, setPreferredOutreachOptions] = useState(
        () => [...PREFERRED_OUTREACH_OPTIONS],
    );
    const [customStartTime, setCustomStartTime] = useState(null);
    const [customEndTime, setCustomEndTime] = useState(null);
    const [customTimeZoneValue, setCustomTimeZoneValue] = useState("CST");
    const [customTimeZoneOpen, setCustomTimeZoneOpen] = useState(false);
    const [customTimeError, setCustomTimeError] = useState("");
    const [customStartTimeError, setCustomStartTimeError] = useState("");
    const [customEndTimeError, setCustomEndTimeError] = useState("");
    const [customSlotConflict, setCustomSlotConflict] = useState(null);
    const [csvUploadData, setCsvUploadData] = useState(null);
    const [restartCsvFileName, setRestartCsvFileName] = useState("");
    const [csvOriginalFileName, setCsvOriginalFileName] = useState("");
    const [csvRemoved, setCsvRemoved] = useState(false);
    const [showUploadIssueModal, setShowUploadIssueModal] = useState(false);
    const [uploadIssueReportUrl, setUploadIssueReportUrl] = useState("");

    const campaignData = campaignDetailsState?.data?.data ?? campaignDetailsState?.data;
    const selectedCampaignTimezone = restartForm?.campaignTimeZone || "CST";
    const timezoneTodayStart = useMemo(
        () => getTimezoneNow(selectedCampaignTimezone).startOf("day"),
        [selectedCampaignTimezone],
    );

    useEffect(() => {
        if (!campaignId || !refBrandId) {
            navigate("/ai-campaigns/campaign-list");
            return;
        }
        dispatch(getAiCampaignDetailsHandler({ campaignId, refBrandId }));
    }, [campaignId, refBrandId]);

    useEffect(() => {
        if (campaignData) {
            setRestartFormKey((k) => k + 1);
            setRestartForm(buildInitialRestartForm(campaignData));
            const outreach = buildPreferredOutreachFromApi(campaignData.outreachWindows || []);
            setPreferredOutreachOptions(outreach.options);
            setRestartCsvFileName(campaignData?.csvFileName || "");
            setCsvOriginalFileName(campaignData?.csvFileName || "");
            setCsvRemoved(false);
            setCsvUploadData(null);
        }
    }, [campaignData]);

    useEffect(() => {
        setRestartForm((prev) => {
            if (!prev) return prev;
            const { campaignStartDate, campaignEndDate, blackoutDates, campaignTimeZone } = prev;
            if (!campaignStartDate || !campaignEndDate) {
                return (blackoutDates || []).length
                    ? { ...prev, blackoutDates: [] }
                    : prev;
            }
            const start = toTimezoneDayStart(campaignStartDate, campaignTimeZone);
            const end = toTimezoneDayStart(campaignEndDate, campaignTimeZone);
            if (start.isAfter(end)) {
                return (blackoutDates || []).length
                    ? { ...prev, blackoutDates: [] }
                    : prev;
            }
            const sundayDateStrs = getSundayDateStringsInRange(start, end);
            const valid = (blackoutDates || []).filter((dateStr) => {
                const d = toTimezoneDayStart(dateStr, campaignTimeZone);
                return (
                    (d.isAfter(start) || d.isSame(start)) &&
                    (d.isBefore(end) || d.isSame(end))
                );
            });
            const merged = [...new Set([...valid, ...sundayDateStrs])].sort();
            if (JSON.stringify(merged) === JSON.stringify(blackoutDates || []))
                return prev;
            return { ...prev, blackoutDates: merged };
        });
    }, [restartForm?.campaignStartDate, restartForm?.campaignEndDate]);

    useEffect(() => {
        if (isUploadIssueResponse(updateAiCampaignState?.data)) {
            setUploadIssueReportUrl(
                getUploadIssueErrorFileUrl(updateAiCampaignState?.data),
            );
            setShowUploadIssueModal(true);
            return;
        }

        if (updateAiCampaignState?.data?.statusCode === 200) {
            message.success(updateAiCampaignState?.message || "Campaign updated successfully");
            dispatch(updateAiCampaignAction.updateAiCampaignInfoReset());
            navigate("/ai-campaigns/manage-ai-campaign", {
                state: { campaignId, refBrandId },
            });
        }
        if (
            updateAiCampaignState?.error &&
            !isUploadIssueResponse(updateAiCampaignState?.data)
        ) {
            message.error(updateAiCampaignState.error);
            dispatch(updateAiCampaignAction.updateAiCampaignInfoReset());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateAiCampaignState?.message, updateAiCampaignState?.error, updateAiCampaignState?.data]);

    const restartInFlight = updateAiCampaignState?.isLoading;
    const scrollToRestartField = useCallback((fieldName) => {
        const selectorByField = {
            coverageStartDate: '[data-error-field="coverageStartDate"]',
            campaignStartDate: '[data-error-field="campaignStartDate"]',
            campaignStartTime: '[data-error-field="campaignStartTime"]',
            campaignEndDate: '[data-error-field="campaignEndDate"]',
            campaignEndTime: '[data-error-field="campaignEndTime"]',
            blackoutDates: '[data-error-field="blackoutDates"]',
            preferredOutreachTimes: '[data-error-field="preferredOutreachTimes"]',
            csvFile: '[data-error-field="csvFile"]',
        };
        const targetContainer = document.querySelector(selectorByField[fieldName]);
        if (!targetContainer) return;
        targetContainer.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusableTarget =
            targetContainer.matches("input,select,textarea,button,[tabindex]")
                ? targetContainer
                : targetContainer.querySelector("input, textarea, select, button, [role='combobox'], [tabindex]");
        if (focusableTarget && typeof focusableTarget.focus === "function") {
            focusableTarget.focus({ preventScroll: true });
        }
    }, []);

    const handleCsvFileSelected = async (file) => {
        const fileName = String(file?.name || "").toLowerCase();
        if (!fileName.endsWith(".csv")) {
            message.error("Only CSV files are accepted. Please upload a .csv file.");
            return false;
        }
        if (Number(file?.size || 0) / 1024 / 1024 > 10) {
            message.error("File size exceeds the maximum allowed limit.");
            return false;
        }

        const adminId = getItem("adminId");
        const originalName = String(file?.name || "census.csv");
        const lastDotIndex = originalName.lastIndexOf(".");
        const baseName = lastDotIndex > -1 ? originalName.slice(0, lastDotIndex) : originalName;
        const extension = lastDotIndex > -1 ? originalName.slice(lastDotIndex) : ".csv";
        const timestampedFile = new File(
            [file],
            `${baseName}_${dayjs().format("YYYYMMDDHHmmss")}${extension}`,
            { type: file?.type || "text/csv" },
        );
        const formData = new FormData();
        formData.append("file", timestampedFile);
        formData.append("id", Number(adminId));
        formData.append("brandId", refBrandId);
        formData.append("path", "campaign/census");
        appendFileUploadModuleContext(formData, {
          module: FILE_UPLOAD_MODULES.CAMPAIGNS,
        });

        try {
            const response = await axios.post(`${API_URL}/file/upload`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            setCsvUploadData(response?.data?.data || response?.data || null);
            setCsvOriginalFileName(originalName);
            setCsvRemoved(false);
            setRestartCsvFileName("");
            // message.success(response?.data?.message || "CSV uploaded successfully");
            return true;
        } catch (uploadError) {
            const statusCode = uploadError?.response?.status;
            const uploadErrorMessage =
                uploadError?.response?.data?.message ||
                (statusCode === 405
                    ? MODULE_ACCESS_DENIED_MESSAGE
                    : "CSV upload failed");

            if (statusCode === 405) {
                message.warning(uploadErrorMessage);
            } else {
                message.error(uploadErrorMessage);
            }
            return false;
        }
    };

    const handleDownloadUploadIssueReport = () => {
        if (!uploadIssueReportUrl) {
            message.error("Report file is not available");
            return;
        }

        const link = document.createElement("a");
        link.href = uploadIssueReportUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download = "upload-error-report.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // dispatch(updateAiCampaignAction.updateAiCampaignInfoReset());
    };

    const handleSubmit = () => {
        if (!campaignId || !refBrandId || !campaignData || !restartForm) return;
        setRestartSubmitCount((c) => c + 1);
        setRestartValidation({});
        const rf = restartForm;
        const missingField = RESTART_REQUIRED_FIELD_ORDER.find((fieldName) => !rf[fieldName]);
        if (missingField) {
            message.error("Please complete all date and time fields");
            scrollToRestartField(missingField);
            return;
        }

        const start = combineCampaignDateTime(
            rf.campaignStartDate,
            rf.campaignStartTime,
            rf.campaignTimeZone,
        );
        const end = combineCampaignDateTime(rf.campaignEndDate, rf.campaignEndTime, rf.campaignTimeZone);
        const now = getTimezoneNow(rf.campaignTimeZone);

        if (!start || !end || !start.isValid() || !end.isValid()) {
            message.error("Please complete all date and time fields");
            scrollToRestartField("campaignStartDate");
            return;
        }

        if (end.isBefore(now)) {
            setRestartValidation({
                campaignEndTime:
                    "Campaign end date/time must be current or future.",
            });
            scrollToRestartField("campaignEndTime");
            return;
        }

        if (end.diff(start, "minute") < 24 * 60) {
            setRestartValidation({
                campaignEndTime:
                    "Campaign must span at least 24 hours — adjust end date and/or end time",
            });
            scrollToRestartField("campaignEndTime");
            return;
        }

        if (
            !validateAtLeastOneNonBlackoutDay(
                rf.campaignStartDate,
                rf.campaignEndDate,
                rf.blackoutDates || [],
                rf.campaignTimeZone,
            )
        ) {
            setRestartValidation({
                blackoutDates:
                    "At least one non-blackout day must exist within the campaign window.",
            });
            scrollToRestartField("blackoutDates");
            return;
        }

        if ((rf.preferredOutreachTimes || []).length === 0) {
            setRestartValidation({
                preferredOutreachTimes:
                    "At least one preferred outreach time window is required.",
            });
            scrollToRestartField("preferredOutreachTimes");
            return;
        }
        const hasCsvFile = !!(csvUploadData?.signedUrl || (restartCsvFileName && !csvRemoved));
        if (!hasCsvFile) {
            setRestartValidation({
                csvFile: "Census file is required to restart campaign.",
            });
            scrollToRestartField("csvFile");
            return;
        }
        if (restartInFlight) return;

        const updatePayload = buildRestartCampaignUpdatePayload({
            campaignData,
            restartForm,
            campaignId,
            refBrandId,
            preferredOutreachOptions,
        });
        if (!updatePayload) return;
        const payloadCsvFileName = csvUploadData?.signedUrl
            ? csvOriginalFileName
            : ((!csvRemoved && restartCsvFileName) ? restartCsvFileName : "");
        if (payloadCsvFileName) {
            updatePayload.csvFileName = payloadCsvFileName;
        }
        if (csvUploadData?.signedUrl) {
            updatePayload.csvFileUrl = csvUploadData.signedUrl;
        } else if (csvRemoved) {
            updatePayload.csvFileUrl = "";
            updatePayload.csvFileName = "";
        }

        dispatch(
            updateAiCampaignHandler(
                updatePayload
            ),
        );
    };

    const handleCancel = () => {
        navigate("/ai-campaigns/manage-ai-campaign", {
            state: { campaignId, refBrandId },
        });
    };

    // Clear inline end datetime validation when user changes inputs
    useEffect(() => {
        if (
            !restartValidation?.campaignEndTime ||
            !restartForm ||
            !restartForm.campaignStartDate
        )
            return;
        setRestartValidation((prev) => {
            const next = { ...prev };
            delete next.campaignEndTime;
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        restartForm?.campaignStartDate,
        restartForm?.campaignStartTime,
        restartForm?.campaignEndDate,
        restartForm?.campaignEndTime,
    ]);

    // Clear inline blackout validation when dates/blackouts change
    useEffect(() => {
        if (!restartValidation?.blackoutDates) return;
        setRestartValidation((prev) => {
            if (!prev?.blackoutDates) return prev;
            const { blackoutDates, ...rest } = prev;
            return rest;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restartForm?.campaignStartDate, restartForm?.campaignEndDate, restartForm?.blackoutDates]);

    useEffect(() => {
        if (!restartValidation?.preferredOutreachTimes) return;
        setRestartValidation((prev) => {
            if (!prev?.preferredOutreachTimes) return prev;
            const { preferredOutreachTimes, ...rest } = prev;
            return rest;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restartForm?.preferredOutreachTimes]);

    useEffect(() => {
        if (!restartValidation?.csvFile) return;
        const hasCsvFile = !!(csvUploadData?.signedUrl || (restartCsvFileName && !csvRemoved));
        if (!hasCsvFile) return;
        setRestartValidation((prev) => {
            if (!prev?.csvFile) return prev;
            const { csvFile, ...rest } = prev;
            return rest;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [csvUploadData?.signedUrl, restartCsvFileName, csvRemoved]);

    const disableRestartPastDates = (current) =>
        !!current &&
        toTimezoneDayStart(current, restartForm?.campaignTimeZone).isBefore(timezoneTodayStart);

    const disableRestartEndDates = (current) => {
        if (!current) return false;
        const currentDay = toTimezoneDayStart(current, restartForm?.campaignTimeZone);
        if (currentDay.isBefore(timezoneTodayStart)) return true;
        if (!restartForm?.campaignStartDate) return false;

        // Same calendar day as start is not allowed — require next day or later
        return !currentDay.isAfter(
            toTimezoneDayStart(restartForm.campaignStartDate, restartForm?.campaignTimeZone),
        );
    };

    const handleRestartStartDateChange = (value) => {
        setRestartForm((prev) => {
            if (!prev) return prev;
            let campaignEndDate = prev.campaignEndDate;
            if (value && prev.campaignEndDate) {
                const startDay = toTimezoneDayStart(value, prev.campaignTimeZone);
                const endDay = toTimezoneDayStart(prev.campaignEndDate, prev.campaignTimeZone);
                if (!endDay.isAfter(startDay)) {
                    campaignEndDate = startDay.add(1, "day");
                }
            }
            return { ...prev, campaignStartDate: value, campaignEndDate };
        });
    };

    const handleRestartEndDateChange = (value) => {
        setRestartForm((prev) => {
            if (!prev) return prev;
            if (!value) return { ...prev, campaignEndDate: value };

            if (prev.campaignStartDate) {
                const startDay = toTimezoneDayStart(prev.campaignStartDate, prev.campaignTimeZone);
                const pickDay = toTimezoneDayStart(value, prev.campaignTimeZone);

                // Same day as start (or earlier) is not allowed — bump to next day
                if (!pickDay.isAfter(startDay)) {
                    return { ...prev, campaignEndDate: startDay.add(1, "day") };
                }
            }
            return { ...prev, campaignEndDate: value };
        });
    };

    const handleRestartSelectSlot = (slotInfo) => {
        if (!restartForm?.campaignStartDate || !restartForm?.campaignEndDate)
            return;
        const slots =
            slotInfo?.slots || (slotInfo?.start ? [slotInfo.start] : []);
        if (!slots.length) return;
        const rangeStart = toTimezoneDayStart(restartForm.campaignStartDate, restartForm.campaignTimeZone);
        const rangeEnd = toTimezoneDayStart(restartForm.campaignEndDate, restartForm.campaignTimeZone);
        if (rangeStart.isAfter(rangeEnd)) return;
        const dateStrs = slots
            .map((d) => toTimezoneDayStart(d, restartForm.campaignTimeZone))
            .filter((d) => !d.isBefore(timezoneTodayStart))
            .filter(
                (d) =>
                    (d.isAfter(rangeStart) || d.isSame(rangeStart)) &&
                    (d.isBefore(rangeEnd) || d.isSame(rangeEnd)),
            )
            .map((d) => d.format("YYYY-MM-DD"));
        if (!dateStrs.length) return;

        setRestartForm((prev) => {
            if (!prev) return prev;
            const newSet = new Set(prev.blackoutDates || []);
            dateStrs.forEach((dateStr) => {
                if (newSet.has(dateStr)) newSet.delete(dateStr);
                else newSet.add(dateStr);
            });
            return { ...prev, blackoutDates: [...newSet].sort() };
        });
    };

    const handleAddCustomTimeWindow = () => {
        setCustomTimeError("");
        setCustomStartTimeError("");
        setCustomEndTimeError("");
        if (!customStartTime) {
            setCustomStartTimeError("Start Time is required.");
            return;
        }
        if (!customEndTime) {
            setCustomEndTimeError("End Time is required.");
            return;
        }

        const start = dayjs(customStartTime);
        const end = dayjs(customEndTime);
        if (!start.isValid() || !end.isValid()) {
            message.error("Invalid start or end time.");
            return;
        }
        if (!end.isAfter(start)) {
            setCustomTimeError("Start time must be earlier than end time.");
            setCustomStartTimeError("Start time must be earlier than end time.");
            setCustomEndTimeError("End time must be later than start time.");
            return;
        }

        const key = `custom_${start.format("HHmm")}-${end.format("HHmm")}_${customTimeZoneValue}`;
        const label = `${start.format("h:mm a")} - ${end.format("h:mm a")} ${customTimeZoneValue}`;
        const newStartMinutes = start.hour() * 60 + start.minute();
        const newEndMinutes = end.hour() * 60 + end.minute();

        const conflictingSelectedOptions = preferredOutreachOptions.filter((o) => {
            const isSelected = (restartForm?.preferredOutreachTimes || []).some((selectedItem) => selectedItem.key === o.key);
            if (!isSelected) return false;
            if (o.timezone !== customTimeZoneValue) return false;
            const existingStart = dayjs(`1970-01-01 ${o.startTime}`);
            const existingEnd = dayjs(`1970-01-01 ${o.endTime}`);
            if (!existingStart.isValid() || !existingEnd.isValid()) return false;
            const existingStartMinutes = existingStart.hour() * 60 + existingStart.minute();
            const existingEndMinutes = existingEnd.hour() * 60 + existingEnd.minute();
            return newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes;
        });

        const exactDuplicate = preferredOutreachOptions.some((o) =>
            o.startTime === start.format("HH:mm:ss") &&
            o.endTime === end.format("HH:mm:ss") &&
            o.timezone === customTimeZoneValue
        );
        if (exactDuplicate) {
            setCustomTimeError("This slot already exists.");
            return;
        }

        const newOpt = {
            key,
            label,
            isCustome: true,
            startTime: start.format("HH:mm:ss"),
            endTime: end.format("HH:mm:ss"),
            timezone: customTimeZoneValue,
        };

        const applyCustomSlot = (conflictKeys = []) => {
            setPreferredOutreachOptions((prev) => {
                if (prev.some((o) => o.key === key)) return prev;
                return [...prev, newOpt];
            });

            setRestartForm((prev) => {
                if (!prev) return prev;
                const nextSelected = (prev.preferredOutreachTimes || [])
                    .filter((item) => !conflictKeys.includes(item.key));
                if (!nextSelected.some((item) => item.key === key)) {
                    nextSelected.push({
                        key: newOpt.key,
                        startTime: newOpt.startTime,
                        endTime: newOpt.endTime,
                        timezone: newOpt.timezone,
                        isCustom: true,
                    });
                }
                return { ...prev, preferredOutreachTimes: nextSelected };
            });
            setCustomStartTime(null);
            setCustomEndTime(null);
            setCustomTimeError("");
            setCustomStartTimeError("");
            setCustomEndTimeError("");
        };

        if (conflictingSelectedOptions.length) {
            setCustomSlotConflict({
                option: newOpt,
                conflictKeys: conflictingSelectedOptions.map((opt) => opt.key),
                apply: applyCustomSlot,
            });
            return;
        }

        applyCustomSlot();
    };

    const handleDeletePreferredOutreachOption = (key) => {
        setPreferredOutreachOptions((prev) => prev.filter((o) => o.key !== key));
        setRestartForm((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                preferredOutreachTimes: (prev.preferredOutreachTimes || []).filter(
                    (item) => item.key !== key,
                ),
            };
        });
    };
    const handlePreferredOutreachToggle = (option, checked) => {
        if (!restartForm) return;
        if (!checked) {
            setRestartForm((prev) => {
                if (!prev) return prev;
                const nextList = (prev.preferredOutreachTimes || []).filter((item) => item.key !== option.key);
                return { ...prev, preferredOutreachTimes: nextList };
            });
            return;
        }

        const nextSlot = {
            key: option.key,
            startTime: option.startTime,
            endTime: option.endTime,
            timezone: option.timezone,
            isCustom: !!option.isCustome,
        };
        const nextMinutes = toSlotMinutes(nextSlot);
        if (!nextMinutes || nextMinutes.startMinutes >= nextMinutes.endMinutes) {
            setCustomTimeError("Start time must be earlier than end time.");
            return;
        }

        const selectedSlots = restartForm.preferredOutreachTimes || [];
        const exactDuplicate = selectedSlots.find((item) => isSameSlot(item, nextSlot));
        if (exactDuplicate) {
            setCustomTimeError("This slot already exists.");
            return;
        }

        const conflictKeys = selectedSlots
            .filter((item) => slotsOverlap(item, nextSlot))
            .map((item) => item.key);

        const applyPreferredSlot = (keysToReplace = []) => {
            setRestartForm((prev) => {
                if (!prev) return prev;
                const nextList = (prev.preferredOutreachTimes || [])
                    .filter((item) => !keysToReplace.includes(item.key))
                    .concat(nextSlot);
                return { ...prev, preferredOutreachTimes: nextList };
            });
        };

        if (conflictKeys.length) {
            setCustomSlotConflict({
                option: nextSlot,
                conflictKeys,
                apply: applyPreferredSlot,
            });
            return;
        }

        applyPreferredSlot();
    };

    const restartDayPropGetter = useMemo(() => {
        return (date) => {
            if (!restartForm) return {};
            const d = toTimezoneDayStart(date, restartForm.campaignTimeZone);
            const dateStr = d.format("YYYY-MM-DD");
            if (!restartForm.campaignStartDate || !restartForm.campaignEndDate)
                return {};
            const start = toTimezoneDayStart(restartForm.campaignStartDate, restartForm.campaignTimeZone);
            const end = toTimezoneDayStart(restartForm.campaignEndDate, restartForm.campaignTimeZone);
            if (start.isAfter(end)) return {};
            const isInCampaignRange =
                (d.isAfter(start) || d.isSame(start)) &&
                (d.isBefore(end) || d.isSame(end));
            if ((restartForm.blackoutDates || []).includes(dateStr)) {
                return { className: "blackout-day" };
            }
            if (
                isInCampaignRange
            ) {
                const classes = ["campaign-range-day"];
                if (d.isSame(start)) classes.push("campaign-start-day");
                if (d.isSame(end)) classes.push("campaign-end-day");
                return { className: classes.join(" ") };
            }
            return {};
        };
    }, [restartForm]);

    const handleRestartCampaignTimezoneChange = (timezoneValue) => {
        setRestartForm((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                campaignTimeZone: timezoneValue,
                campaignStartDate: null,
                campaignStartTime: null,
                campaignEndDate: null,
                campaignEndTime: null,
                blackoutDates: [],
            };
        });
    };
    const primaryConflictSlot = useMemo(() => {
        if (!customSlotConflict?.conflictKeys?.length) return null;
        const conflictKey = customSlotConflict.conflictKeys[0];
        const selectedConflict = (restartForm?.preferredOutreachTimes || []).find((slot) => slot.key === conflictKey);
        if (selectedConflict) return selectedConflict;
        return preferredOutreachOptions.find((slot) => slot.key === conflictKey) || null;
    }, [customSlotConflict, restartForm?.preferredOutreachTimes, preferredOutreachOptions]);

    const conflictExistingLabel = formatOutreachSlotLabel(primaryConflictSlot);
    const conflictNewLabel = formatOutreachSlotLabel(customSlotConflict?.option);

    if (campaignDetailsState?.isLoading || !campaignData) {
        return <Loader loaderTransform="loaderTransform" />;
    }

    return (
        <div className="rightside">
            <div className="rightcontent">
                <p>
                    <strong>Restart Campaign</strong>
                </p>
                <CustomBreadcrumb
                    items={[
                        { text: BreadCrum.HOME },
                        { text: "AI Campaigns" },
                        {
                            text: "Campaign List",
                            onClick: () => navigate("/ai-campaigns/campaign-list"),
                            active: true,
                        },
                        {
                            text: "Restart Campaign",
                            active: true,
                        }
                    ]}
                />

                {/* <div className="usercard mb-30">
                    <div className="campaignDetailsCard d-flex flex-column">
                        <span className="campaigncardHeader mb-10">
                            Restart Campaign
                        </span>
                        <p className="mb-0">Resume all communication and update campaign dates</p>
                    </div> */}
                <div className="usercard mb-30">
                    <div className="campaignDetailsCard d-flex flex-column">
                        <span className="campaigncardHeader mb-10">
                            Restart Campaign
                        </span>
                        <p className="mb-0">Resume all communication and update campaign dates</p>
                    </div>
                    <hr className="m-0" />
                    <div className="campaigncardBody login-form">
                        {restartForm && (
                            <React.Fragment key={restartFormKey}>
                                <div className="form-group noicon visibleicon" data-error-field="coverageStartDate">
                                    <label htmlFor="restart-coverageStart">Coverage Start Date</label>
                                    <DatePicker
                                        id="restart-coverageStart"
                                        className="w-100 customDatePicker"
                                        placeholder="MM / DD / YYYY"
                                        format="MM / DD / YYYY"
                                        value={restartForm.coverageStartDate}
                                        defaultPickerValue={getTimezoneNow(restartForm.campaignTimeZone)}
                                        onChange={(date) =>
                                            setRestartForm((p) =>
                                                p ? { ...p, coverageStartDate: date } : p,
                                            )
                                        }
                                        disabledDate={disableRestartPastDates}
                                        suffixIcon={<CalanderIcon />}
                                    />
                                </div>
                                <div />
                                <div className="form-group noicon visibleicon" data-error-field="campaignStartDate">
                                    <label htmlFor="restart-campaignStart">Campaign Start Date</label>
                                    <DatePicker
                                        id="restart-campaignStart"
                                        className="w-100 customDatePicker"
                                        placeholder="Select Start Date"
                                        format="MM / DD / YYYY"
                                        value={restartForm.campaignStartDate}
                                        defaultPickerValue={getTimezoneNow(restartForm.campaignTimeZone)}
                                        onChange={handleRestartStartDateChange}
                                        disabledDate={disableRestartPastDates}
                                        suffixIcon={<CalanderIcon />}
                                    />
                                </div>
                                <div className="form-group noicon visibleicon" data-error-field="campaignStartTime">
                                    <label htmlFor="restart-campaignStartTime">Campaign Start Time</label>
                                    <div className="d-flex align-items-center gap-10">
                                        <TimePicker
                                            id="restart-campaignStartTime"
                                            className="w-100 customTimePicker"
                                            placeholder="Select Start Time"
                                            format="HH:mm"
                                            needConfirm={false}
                                            showNow={false}
                                            value={restartForm.campaignStartTime}
                                            defaultOpenValue={getTimezoneNow(restartForm.campaignTimeZone)}
                                            disabledTime={() =>
                                                buildDisabledPastTime(
                                                    restartForm.campaignStartDate,
                                                    restartForm.campaignTimeZone,
                                                )
                                            }
                                            onChange={(time) =>
                                                setRestartForm((p) =>
                                                    p ? { ...p, campaignStartTime: time } : p,
                                                )
                                            }
                                            suffixIcon={<TimeIcon />}
                                        />
                                        <Select
                                            className="customSelect w-100 timeZoneSelect"
                                            placeholder="Select Time Zone"
                                            value={restartForm.campaignTimeZone}
                                            onChange={handleRestartCampaignTimezoneChange}
                                            onOpenChange={setRestartStartTzOpen}
                                            menuItemSelectedIcon={<TimezoneSelectedIcon />}
                                            suffixIcon={<ChevronIcon isOpen={restartStartTzOpen} />}
                                            options={TIMEZONE_OPTIONS}
                                        />
                                    </div>
                                </div>
                                <div className="form-group noicon visibleicon" data-error-field="campaignEndDate">
                                    <label htmlFor="restart-campaignEnd">Campaign End Date</label>
                                    <DatePicker
                                        id="restart-campaignEnd"
                                        className="w-100 customDatePicker"
                                        placeholder="Select End Date"
                                        format="MM / DD / YYYY"
                                        value={restartForm.campaignEndDate}
                                        defaultPickerValue={getTimezoneNow(restartForm.campaignTimeZone)}
                                        onChange={handleRestartEndDateChange}
                                        disabledDate={disableRestartEndDates}
                                        suffixIcon={<CalanderIcon />}
                                    />
                                </div>
                                <div className="form-group noicon visibleicon" data-error-field="campaignEndTime">
                                    <label htmlFor="restart-campaignEndTime">Campaign End Time</label>
                                    <div className="d-flex align-items-center gap-10">
                                        <TimePicker
                                            id="restart-campaignEndTime"
                                            className="w-100 customTimePicker"
                                            placeholder="Select End Time"
                                            format="HH:mm"
                                            needConfirm={false}
                                            showNow={false}
                                            value={restartForm.campaignEndTime}
                                            defaultOpenValue={getTimezoneNow(restartForm.campaignTimeZone)}
                                            disabledTime={() =>
                                                buildDisabledPastTime(
                                                    restartForm.campaignEndDate,
                                                    restartForm.campaignTimeZone,
                                                )
                                            }
                                            onChange={(time) =>
                                                setRestartForm((p) =>
                                                    p ? { ...p, campaignEndTime: time } : p,
                                                )
                                            }
                                            suffixIcon={<TimeIcon />}
                                        />
                                        <Select
                                            className="customSelect w-100 timeZoneSelect"
                                            placeholder="Select Time Zone"
                                            value={restartForm.campaignTimeZone}
                                            onChange={handleRestartCampaignTimezoneChange}
                                            onOpenChange={setRestartEndTzOpen}
                                            menuItemSelectedIcon={<TimezoneSelectedIcon />}
                                            suffixIcon={<ChevronIcon isOpen={restartEndTzOpen} />}
                                            options={TIMEZONE_OPTIONS}
                                        />
                                    </div>
                                    {restartValidation?.campaignEndTime && (
                                        <div
                                            className="text-danger small mt-1"
                                            style={{ color: "red" }}
                                        >
                                            {restartValidation.campaignEndTime}
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        )}
                    </div>

                    {restartForm?.campaignStartDate && restartForm?.campaignEndDate && (
                        <>
                            <hr className="m-0" />
                            <div className="campaignDetailsCard d-flex flex-column" data-error-field="blackoutDates">
                                <p className="mb-0 fs-16 mb-3">Blackout Dates (Optional)</p>
                                <div className={`blackout-calendar-container blackout-calendar has-campaign-range`}>
                                    <Calendar
                                        localizer={localizer}
                                        events={[]}
                                        defaultView="month"
                                        views={["month"]}
                                        selectable={true}
                                        onSelectSlot={handleRestartSelectSlot}
                                        dayPropGetter={restartDayPropGetter}
                                        toolbar
                                        style={{ height: 300 }}
                                    />
                                </div>
                                {restartValidation?.blackoutDates && (
                                    <div
                                        className="text-danger small mt-2 px-3"
                                        style={{ color: "red" }}
                                    >
                                        {restartValidation.blackoutDates}
                                    </div>
                                )}
                                <div className="d-flex flex-column gap-2">
                                    <div className="blackout-dates-info">
                                        <InfoIcon />
                                        <p className="mb-2 mt-2 text-muted small">Blue dates = blackout days (no AI Agent contact). Sundays in the campaign range are auto-selected as blackout. Click in-range dates to toggle blackout.</p>
                                    </div>
                                    <hr className="m-0" />
                                    {(restartForm.blackoutDates || []).length > 0 && (
                                        <div className="">
                                            <div className="campaignDetailsCard d-flex flex-column blackout-dates-list p-0 gap-20">
                                                <p className="mb-0 fs-16 ">Blackout Dates (Optional)</p>
                                                <div className="blackout-calendar-arrays">
                                                    <div className="d-flex gap-10 mb-2 flex-wrap">
                                                        {(restartForm.blackoutDates || []).map((d) => (
                                                            <span key={d} className="dateCard  me-1 mb-1 fs-14">
                                                                {dayjs(d).format("MMMM D, YYYY")}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="usercard mb-30">
                    <div className="campaignDetailsCard d-flex flex-column">
                        <span className="campaigncardHeader mb-10">
                            Preferred Time to Outreach*
                        </span>
                        <p className="mb-0">
                            AI Agent will contact perspective members in the preferred time windows
                        </p>
                    </div>
                    <hr className="m-0" />
                    <div className="campaingcustomTime login-form" data-error-field="preferredOutreachTimes">
                        <div className="d-flex flex-wrap gap-20 w-100">
                            {preferredOutreachOptions.map((opt) => {
                                const id = `restartPreferredOutreachTime_${opt.key}`;
                                const checked = (restartForm?.preferredOutreachTimes || []).some(
                                    (item) => item.key === opt.key,
                                );
                                return (
                                    <label key={opt.key} className="preferredTimeOption" htmlFor={id}>
                                        <input
                                            id={id}
                                            className="preferredTimeOption__input"
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => handlePreferredOutreachToggle(opt, e.target.checked)}
                                        />
                                        <span className="preferredTimeOption__box" aria-hidden="true" />
                                        <span className="preferredTimeOption__text">{opt.label}</span>
                                        {opt?.isCustome && (
                                            <button
                                                type="button"
                                                className="preferredTimeOption__delete d-flex align-items-center"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleDeletePreferredOutreachOption(opt.key);
                                                }}
                                            >
                                                <div className="trashBorder"></div>
                                                <Trash color={"#CD6422"} />
                                            </button>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                        {restartSubmitCount > 0 && restartValidation?.preferredOutreachTimes && (
                            <div className="text-danger small mt-2" style={{ color: "red" }}>
                                {restartValidation.preferredOutreachTimes}
                            </div>
                        )}

                        <div className="d-flex flex-column gap-20">
                            <div className="campaigncardHeader mb-10">
                                Add Custom Time Window
                            </div>
                            <div className="d-flex gap-20 w-100">
                                <div className="form-group noicon visibleicon">
                                    <label htmlFor="restartCustomStartTime">Start Time</label>
                                    <div className="d-flex align-items-center gap-10">
                                        <TimePicker
                                            id="restartCustomStartTime"
                                            className="w-100 customTimePicker"
                                            placeholder="Select Start Time"
                                            format="HH:mm"
                                            value={customStartTime}
                                            onChange={setCustomStartTime}
                                            suffixIcon={<TimeIcon />}
                                            needConfirm={false}
                                            showNow={false}
                                        />
                                    </div>
                                </div>
                                <div className="form-group noicon visibleicon">
                                    <label htmlFor="restartCustomEndTime">End Time</label>
                                    <div className="d-flex align-items-center gap-10">
                                        <TimePicker
                                            id="restartCustomEndTime"
                                            className="w-100 customTimePicker"
                                            placeholder="Select End Time"
                                            format="HH:mm"
                                            value={customEndTime}
                                            onChange={setCustomEndTime}
                                            suffixIcon={<TimeIcon />}
                                            needConfirm={false}
                                            showNow={false}
                                        />
                                    </div>
                                </div>
                                <div
                                    className="form-group noicon visibleicon"
                                    style={{ width: "100%", maxWidth: "120px" }}
                                >
                                    <label htmlFor="restartCustomTimeZone">Time Zone</label>
                                    <div className="d-flex align-items-center gap-10">
                                        <Select
                                            id="restartCustomTimeZone"
                                            className="customSelect w-100 timeZoneSelect"
                                            placeholder="Select Time Zone"
                                            value={customTimeZoneValue}
                                            onChange={setCustomTimeZoneValue}
                                            onOpenChange={setCustomTimeZoneOpen}
                                            menuItemSelectedIcon={<TimezoneSelectedIcon />}
                                            suffixIcon={<ChevronIcon isOpen={customTimeZoneOpen} />}
                                            options={TIMEZONE_OPTIONS}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex" style={{ alignItems: "end" }}>
                                    <button
                                        type="button"
                                        className="primarybtn"
                                        onClick={handleAddCustomTimeWindow}
                                    >
                                        <div className="plus"></div>
                                        ADD
                                    </button>
                                </div>
                            </div>
                            {(!!customStartTimeError || !!customEndTimeError || !!customTimeError) && (
                                <div className="text-danger small mt-1">{customStartTimeError || customEndTimeError || customTimeError}</div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="usercard mb-30">
                    <div className="campaignDetailsCard d-flex justify-content-between align-items-center ">
                        <span className="campaigncardHeader mb-10">
                            CSV Upload - Census*
                        </span>
                    </div>
                    <hr className="m-0" />
                    <div className="campaignDetailsCard display-block" data-error-field="csvFile">
                        <CsvUploadDragger
                            accept=".csv"
                            maxSizeMB={10}
                            uploadText={(<><u>Click to upload CSV</u> or drag and drop</>)}
                            supportedFormatsText="Supported formats : CSV"
                            infoText="The census data will be used by the AI Agent to identify and contact perspective members. Each member will be assigned a member portal login upon campaign launch."
                            onFileSelected={handleCsvFileSelected}
                            initialFileName={restartCsvFileName}
                            onFileRemoved={() => {
                                setCsvUploadData(null);
                                setRestartCsvFileName("");
                                setCsvRemoved(true);
                            }}
                        />
                        {restartValidation?.csvFile && (
                            <div className="text-danger small mt-2" style={{ color: "red" }}>
                                {restartValidation.csvFile}
                            </div>
                        )}
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-3 mt-4">
                    <button
                        type="button"
                        className="campaign-modal-cancel-btn"
                        onClick={handleCancel}
                        disabled={restartInFlight}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="campaign-modal-action-btn"
                        onClick={handleSubmit}
                        disabled={restartInFlight || !restartForm}
                    >
                        <RestartIcon />
                        {restartInFlight ? "Restarting…" : "Restart Campaign"}
                    </button>
                </div>
            </div>
            <CampaignModal
                open={showUploadIssueModal}
                onCancel={() => {
                    setShowUploadIssueModal(false);
                    dispatch(updateAiCampaignAction.updateAiCampaignInfoReset());
                }}
                onAction={handleDownloadUploadIssueReport}
                title="Upload Issue Found"
                subtitle="Some user data is missing in the uploaded file."
                actionLabel="Download Report"
                actionIcon={
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z"
                            fill="#fff"
                        />
                    </svg>
                }
            >
                <div
                    style={{
                        backgroundColor: "#f7f7f8",
                        border: "1px solid #e5e7eb",
                        borderRadius: "10px",
                        padding: "20px 22px",
                    }}
                >
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <InfoIcon />
                        <strong style={{ color: "#162c4e", fontSize: "15px" }}>
                            What you need to do:
                        </strong>
                    </div>
                    <div style={{ color: "#374151", lineHeight: 1.6 }}>
                        {getUploadIssueMessage(updateAiCampaignState?.data)}
                    </div>
                </div>
            </CampaignModal>
            <CampaignModal
                open={!!customSlotConflict}
                onCancel={() => setCustomSlotConflict(null)}
                onAction={() => {
                    if (!customSlotConflict) return;
                    customSlotConflict.apply(customSlotConflict.conflictKeys);
                    setCustomSlotConflict(null);
                }}
                title="Time Conflict Detected"
                subtitle="This time overlaps with an existing window."
                actionLabel="Replace"
                cancelLabel="Keep Existing"
            >
                <div className="campaign-modal-info-box">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <InfoIcon />
                        <strong className="campaign-modal-info-title">
                            This action will:
                        </strong>
                    </div>
                    <p className="campaign-modal-info-text mb-3">
                        {`This time slot overlaps with your existing slot (${conflictExistingLabel || "selected slot"}) and the new slot (${conflictNewLabel || "new slot"}).`}
                    </p>
                    <span>Do you want to replace it?</span>
                </div>
            </CampaignModal>
        </div >
    );
};

export default RestartCampaignPage;
